import {
	LeftOutlined,
	SaveOutlined,
	PlusSquareOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { t } from "@lingui/macro";
import { Tabs, Tooltip, message } from "antd";
import CarForm, {
	CarFormComponentProps,
} from "components/dedicated/cars/create/CarForm";
import { createRef, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { uuid } from "short-uuid";
import {
	defaultCar,
	resetListCreatingCar,
	selectCarCreatingList,
	setListCreatingCar,
	clearListCreatingCar,
} from "../redux/creating-cars.slice";
import "./style.scss";

import { Button, Row } from "antd";

import validatePerms from "utils/validate-perms";
import { PERMISSION } from "../../../constants";
import { useEffect } from "react";
import { Car, MasterDataOption } from "../types";
import { createCarsAPI } from "service/apis";
import { getCustomerByIdAPI } from "service/apis/customer";
import { APP_PATH } from "constants/path";
import {
	getDefaultMerchantAndStoreIds,
	getDiscount,
	getTimeExpired,
	translate,
} from "utils";
import {
	CAR_BODY_TYPE,
	CAR_MEDIA_RULE,
	CAR_STATE,
	MAX_CAR_ADDED,
} from "constants/constants";
import { PhotoItemProps } from "components/upload-multiple-image-dnd";
import useQuery from "hooks/useQuery";
import { getFormRequestDetailsAPI } from "service/apis/form-request";
import { FORM_REQUEST_DISCOUNT_TYPE } from "features/webapp/form-request/type";

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

const customDataPhotos = (listPhoto: PhotoItemProps[]) => {
	const listExternal = listPhoto.filter(
		(item: PhotoItemProps) => item.classifiedType === "external",
	);
	const listInternal = listPhoto.filter(
		(item: PhotoItemProps) => item.classifiedType === "internal",
	);
	const video = listPhoto.filter(
		(item: PhotoItemProps) => item.classifiedType === "video",
	);
	return [...listExternal, ...listInternal, ...video];
};

export const validateMedia = (media: PhotoItemProps[], callback: any) => {
	const externals: PhotoItemProps[] = [];
	const internals: PhotoItemProps[] = [];
	const videos: PhotoItemProps[] = [];

	const error = {
		internal: "",
		external: "",
		video: "",
	};

	for (let index = 0; index < media.length; index++) {
		if (media[index]?.classifiedType === "external") {
			externals.push(media[index]);
		}
		if (media[index]?.classifiedType === "internal") {
			internals.push(media[index]);
		}
		if (media[index]?.classifiedType === "video") {
			videos.push(media[index]);
		}
	}
	// validate external photos
	if (externals.length < CAR_MEDIA_RULE.external.minLength.value) {
		error.external = CAR_MEDIA_RULE.external.minLength.message;
	}
	if (externals.length > CAR_MEDIA_RULE.external.maxLength.value) {
		error.external = CAR_MEDIA_RULE.external.maxLength.message;
	}
	// validate internal photos
	if (internals.length < CAR_MEDIA_RULE.internal.minLength.value) {
		error.internal = CAR_MEDIA_RULE.internal.minLength.message;
	}
	if (internals.length > CAR_MEDIA_RULE.internal.maxLength.value) {
		error.internal = CAR_MEDIA_RULE.internal.maxLength.message;
	}
	// validate video
	// NOTE: update to video optional
	// if (videos.length === 0) {
	// 	error.video = CAR_MEDIA_RULE.video.required.message;
	// }
	callback(error);
	return !(error.external || error.internal || error.video);
};

const CarCreate = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
	const listCars = useSelector(selectCarCreatingList);
	const [activeKey, setActiveKey] = useState<string>(
		listCars && listCars.length > 0 ? listCars[0].id.toString() : uuid(),
	);
	const [isFirstSubmit, setIsFirstSubmit] = useState(true);

	const query = useQuery();

	const carFormRefs = useRef<React.RefObject<CarFormComponentProps>[]>([]);

	const canUpdateCar = useRef<boolean>(validatePerms([PERMISSION.CAR.UPDATE]));

	useEffect(() => {
		if (listCars) {
			const listIds = listCars.map(car => car.id.toString());
			const newListCar = carFormRefs.current.filter((formRef: any) =>
				listIds.includes(formRef.current?.id),
			);
			carFormRefs.current = newListCar;
		}
	}, [listCars]);

	useEffect(() => {
		handleShowDataFromRequest();
	}, [query.get("serviceRequest")]);

	const getLabelOrValue = (
		type: "label" | "value",
		list: MasterDataOption[],
		key?: string,
	): string | undefined => {
		if (!key) return key;
		for (const option of list) {
			if (type === "label" && option.value === key) return option.label;
			if (type === "value" && option.label === key) return option.value;
		}
		return key;
	};

	const handleShowDataFromRequest = async () => {
		const serviceRequest = query.get("serviceRequest");

		if (serviceRequest) {
			const request = await getFormRequestDetailsAPI(serviceRequest);
			const customer = await getCustomerByIdAPI(request.userId);
			const { merchant, store } = getDefaultMerchantAndStoreIds();
			if (request) {
				const carAttribute = request.carAttribute;
				const photos =
					carAttribute?.images?.map((image, index) => ({
						url: image,
						position: index + 1,
						classifiedType: "external",
					})) || [];
				const internalImages =
					carAttribute?.internalImages?.map((image, index) => ({
						url: image,
						position: index + 1,
						classifiedType: "internal",
					})) || [];
				const video = carAttribute?.video
					? {
							url: carAttribute.video,
							position: 0,
							classifiedType: "video",
					  }
					: undefined;

				let mileage = 0;
				try {
					const mileageStrs = carAttribute?.mileage?.split(" - ");
					if (mileageStrs) {
						if (mileageStrs.length > 1) {
							mileage = Number(mileageStrs[1].replaceAll(",", ""));
						} else {
							mileage = Number(mileageStrs[0].replaceAll(",", ""));
						}
					}
				} catch (_) {}
				const carPhotos = [...photos, ...internalImages];
				if (video) {
					carPhotos.unshift(video);
				}

				const car = {
					brand: carAttribute?.brand.brandId,
					model: carAttribute?.model.modelId,
					photos: carPhotos,
					distanceDriven: mileage,
					isChecked: carAttribute?.isChecked,
					manufactureYear: carAttribute?.manufactureYear,
					fuel: carAttribute?.fuel?.toLowerCase(),
					color: carAttribute?.color?.toLowerCase(),
					transmission: carAttribute?.transmission?.toLowerCase(),
					origin: carAttribute?.origin?.toLowerCase(),
					bodyType: getLabelOrValue(
						"label",
						CAR_BODY_TYPE,
						carAttribute?.bodyType,
					),
					taxType: carAttribute?.taxType?.toLowerCase(),
					sellingPrice: carAttribute?.sellingPrice,
					state: carAttribute?.state
						? CAR_STATE.find((item: any) => item.value === carAttribute?.state)
								?.value
						: undefined,
					price: carAttribute?.sellingPrice,
					discountValue: carAttribute?.discountValue,
					isNegotiation: carAttribute?.isNegotiation,
					discountType: carAttribute?.discountType,
					customer: customer,
					merchant: merchant,
					store: store?.id,
					carStore: store?.id,
					planToSell: request?.planToSell,
				};
				if (carAttribute?.discountType && carAttribute?.discountValue) {
					const { price } = getDiscount(
						carAttribute?.sellingPrice,
						carAttribute?.discountValue,
						carAttribute?.discountType,
					);
					car.discountType = FORM_REQUEST_DISCOUNT_TYPE.PRICE;
					car.discountValue = price;
				}
				const id = uuid();
				dispatch(resetListCreatingCar({ id, car }));
				setActiveKey(id);
			}
		}
	};

	const getFormRef = (id: string) => {
		const carFormRef = carFormRefs.current.find(
			(ref: any) => ref.current?.id === id,
		);
		if (carFormRef) return carFormRef;

		const newCarFormRef = createRef<CarFormComponentProps>();
		carFormRefs.current.push(newCarFormRef);
		return newCarFormRef;
	};

	const onChange = (newActiveKey: string) => {
		setActiveKey(newActiveKey);
	};

	const add = () => {
		const newActiveKey = uuid();
		const newCar = { ...defaultCar, id: newActiveKey };
		if (listCars) {
			dispatch(setListCreatingCar([...listCars, newCar]));
			setActiveKey(newActiveKey);
			window.scrollTo({ top: 0 });
		}
	};

	const remove = (targetKey: TargetKey) => {
		window.dialogConfirm(
			translate("Confirmation"),
			translate("Are you sure you want to delete this tab?"),
			(confirm: boolean) => {
				if (confirm) {
					if (listCars) {
						let newActiveKey: number | string = activeKey;
						let lastIndex = -1;
						listCars.forEach((item, i) => {
							if (item.id === targetKey) {
								lastIndex = i - 1;
							}
						});
						const newPanes = (listCars as Car[]).filter(
							item => item.id !== targetKey,
						);
						if (newPanes.length && newActiveKey === targetKey) {
							if (lastIndex >= 0) {
								newActiveKey = newPanes[lastIndex].id;
							} else {
								newActiveKey = newPanes[0].id;
							}
						}
						dispatch(setListCreatingCar(newPanes));
						setActiveKey(newActiveKey.toString());
					}
				}
			},
		);
	};

	const onEdit = (
		targetKey: React.MouseEvent | React.KeyboardEvent | string,
		action: "add" | "remove",
	) => {
		if (action === "add") {
			add();
		} else {
			remove(targetKey);
		}
	};

	const onChangeForm = (id: string | number, value: any) => {
		if (listCars) {
			const newCars = listCars.map(car => {
				if (car.id === id) {
					if (value.merchant) {
						return { ...car, ...value, store: undefined, carStore: undefined };
					}
					return { ...car, ...value };
				}
				return car;
			});
			dispatch(setListCreatingCar(newCars));
		}
	};

	const validate = (car: Car): boolean => {
		const carRef = carFormRefs.current.find(
			(ref: any) => ref.current.id === car.id,
		);
		if (
			!carRef ||
			!carRef.current ||
			!validateMedia(
				car.photos as PhotoItemProps[],
				carRef.current.setMediaError,
			)
		) {
			window.scrollTo({ top: 0, behavior: "smooth" });
			return false;
		}
		if (
			!car?.brand ||
			!car?.model ||
			!car?.manufactureYear ||
			!car?.price ||
			!car?.merchant?.id ||
			(car?.model === "Other" && !car?.note)
		) {
			message.error(t`Please complete all required fields`);
			return false;
		}

		if (car?.price < 1) return false;

		try {
			if (
				car?.minPrice &&
				Number.parseInt(car?.minPrice.toString()) >
					Number.parseInt(car?.price.toString())
			) {
				message.error(
					t`Selling Price must be greater or equal than Minimum Selling Price`,
				);
				return false;
			}
		} catch (error) {
			return false;
		}
		return true;
	};

	const formatPayload = () => {
		const payload = listCars?.map((car: Car & { planToSell?: number }) => {
			const returnedCar = JSON.parse(JSON.stringify(car));
			delete returnedCar.id;
			returnedCar.merchant = car?.merchant?.id;
			returnedCar.color = returnedCar?.color
				?.toLowerCase()
				.trim()
				.split(" ")
				.join("_");
			returnedCar.fuel = returnedCar?.fuel
				?.toLowerCase()
				.trim()
				.split(" ")
				.join("_");
			returnedCar.state = returnedCar?.state
				?.toLowerCase()
				.trim()
				.split(" ")
				.join("_");
			returnedCar.transmission = returnedCar?.transmission
				?.toLowerCase()
				.trim()
				.split(" ")
				.join("_");
			returnedCar.origin = returnedCar?.origin
				?.toLowerCase()
				.trim()
				.split(" ")
				.join("_");
			returnedCar.bodyType = returnedCar?.bodyType
				?.toLowerCase()
				.trim()
				.split(" ")
				.join("_");
			returnedCar.status = "publish";

			returnedCar.photos = customDataPhotos(returnedCar.photos);
			returnedCar.carStore = car?.store ? car.carStore : undefined;
			if (query.get("serviceRequest")) {
				returnedCar.serviceRequestId = query.get("serviceRequest");
				returnedCar.customer = car?.customer?.id;
				returnedCar.expiredToSell = getTimeExpired(car?.planToSell ?? 0);
			}
			return returnedCar;
		});

		return payload;
	};

	const handleSave = () => {
		if (isFirstSubmit) setIsFirstSubmit(false);
		if (listCars) {
			for (const car of listCars) {
				if (!validate(car)) {
					const carRef = carFormRefs.current.find(
						(ref: any) => ref.current.id === car.id,
					);
					if (carRef && carRef.current) {
						setActiveKey(car.id.toString());
						carRef.current.form.validateFields();
					}
					return;
				}
			}

			const payload = formatPayload();
			if (payload) {
				setIsSubmiting(true);
				createCarsAPI(payload)
					.then(async () => {
						await new Promise(resolve => setTimeout(resolve, 300));
						setIsSubmiting(false);
						dispatch(resetListCreatingCar({}));

						message.success(t`Create successful`);
						navigate(APP_PATH.cars);
					})
					.catch(error => {
						setIsSubmiting(false);
						message.error(translate(error.message));
					});
			}
		}
	};

	const handleClear = () => {
		dispatch(clearListCreatingCar(activeKey));
	};

	return (
		<div>
			<div className="h-full bg-white p-5 my-5 container">
				<Tabs
					hideAdd={
						!!query.get("serviceRequest") ||
						(listCars && listCars.length >= MAX_CAR_ADDED)
					}
					type="editable-card"
					onChange={onChange}
					onEdit={onEdit}
					activeKey={activeKey.toString()}
					addIcon={
						<Tooltip placement="top" title={t`Add more`}>
							<div className="w-full h-full flex justify-center items-center">
								<PlusOutlined />
							</div>
						</Tooltip>
					}
					// tabBarExtraContent={
					// 	canUpdateCar.current && (
					// 		<Button
					// 			icon={<SaveOutlined />}
					// 			type="primary"
					// 			className="flex items-center bg-[#1890ff]"
					// 			onClick={handleSave}
					// 			loading={isSubmiting}
					// 		>
					// 			{t`Publish All`}
					// 		</Button>
					// 	)
					// }
				>
					{listCars?.map(car => (
						<Tabs.TabPane
							closable={listCars?.length > 1}
							forceRender
							tab={car.name.trim() || t`New car`}
							key={car?.id.toString()}
						>
							<CarForm
								car={car}
								onChangeForm={onChangeForm}
								parentRef={getFormRef(car.id.toString())}
								isFirstSubmit={isFirstSubmit}
							/>
						</Tabs.TabPane>
					))}
				</Tabs>
				<Button onClick={handleClear} className="ml-40">{t`Clear`}</Button>
			</div>
			<div className="p-5 my-5 container">
				<Row gutter={8} className="flex justify-end gap-5">
					<Button
						onClick={() => navigate(-1)}
						icon={<LeftOutlined />}
						type="primary"
						className="flex items-center bg-[#595959] border-0"
					>
						{t`Go back`}
					</Button>
					<Button
						onClick={() => add()}
						icon={<PlusSquareOutlined />}
						type="default"
						className="flex items-center"
						disabled={listCars.length >= MAX_CAR_ADDED}
					>
						{t`Add more`}
					</Button>
					<Button
						icon={<SaveOutlined />}
						type="primary"
						className="flex items-center bg-[#1890ff]"
						onClick={handleSave}
						loading={isSubmiting}
					>
						{t`Publish All`}
					</Button>
				</Row>
			</div>
		</div>
	);
};

export default CarCreate;
